/**
 * Module dependencies.
 */

import { ArrowLeft } from 'lucide-react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { ProfileEditor } from 'app/components/pages/settings/profile/profile-editor';
import { Spinner } from 'app/components/ui/spinner';
import { Title } from 'app/components/atoms/title/title';
import { appRoutes } from 'app/routes';
import { useMe } from 'app/hooks/requests/use-me';
import { useTranslation } from 'react-i18next';
import { useUpdateUser, useUpdateUserPassword, useUser } from 'app/hooks/requests/use-admin-users';
import React from 'react';

/**
 * Export `AdministrationUser` component — the profile-like detail/editor for a
 * user, from Administration.
 */

export function AdministrationUser(): JSX.Element {
  const [translate] = useTranslation();
  const navigate = useNavigate();
  const { userId } = useParams();
  const { data: me } = useMe();
  const { data: user, isLoading } = useUser(userId);

  const { mutateAsync: updateUser } = useUpdateUser(userId);
  const { mutateAsync: updatePassword } = useUpdateUserPassword(userId);

  if (me && !me.isAdmin) {
    return <Navigate replace to={appRoutes.dashboard.home.base} />;
  }

  const fullName = user ? [user.firstName, user.lastName].filter(Boolean).join(' ') || user.email : '';

  return (
    <div className={'max-w-[640px] space-y-5'}>
      <button
        className={'flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground'}
        onClick={() => navigate(appRoutes.dashboard.administration.base)}
        type={'button'}
      >
        <ArrowLeft className={'h-4 w-4'} /> {translate('administration.title')}
      </button>

      <Title>{fullName}</Title>

      {isLoading || !user ? (
        <div className={'grid place-items-center py-16'}>
          <Spinner />
        </div>
      ) : (
        <ProfileEditor canEditAdmin onSetPassword={updatePassword} onUpdate={updateUser} user={user} />
      )}
    </div>
  );
}
